// ZIP-upload adapter. Reads a .zip file containing the receipt
// package and unpacks it into an in-memory map.

import JSZip from "jszip";
import { RECEIPT_SYNTHETIC_ROOT } from "./directory.js";
import { isUnsafeArchivePath, detectCommonRoot, normalizeArchivePath } from "./safe-path.js";
import type { LoaderResult } from "./types.js";

export async function loadReceiptFromZip(file: File): Promise<LoaderResult> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(await file.arrayBuffer());
  } catch (err) {
    return {
      error: {
        code: "zip_unreadable",
        message:
          err instanceof Error
            ? `ZIP file could not be opened: ${err.message}`
            : "ZIP file could not be opened."
      }
    };
  }

  const files = new Map<string, string>();
  const fileEntries = Object.values(zip.files).filter((entry) => !entry.dir);
  const root = detectCommonRoot(fileEntries.map((entry) => entry.name));
  for (const entry of fileEntries) {
    // Reject zip-slip before the entry reaches the virtual filesystem. JSZip
    // normalizes entry.name (collapsing `..`), so the traversal survives only
    // in unsafeOriginalName — guard on that, falling back to the safe name.
    const declaredName =
      (entry as { unsafeOriginalName?: string }).unsafeOriginalName ?? entry.name;
    if (isUnsafeArchivePath(declaredName)) {
      return {
        error: {
          code: "zip_unsafe_entry",
          message: `ZIP contains a path that escapes the receipt root: ${declaredName}`
        }
      };
    }
    // Canonicalize (`a/./b`, `a//b`, leading `./`) so equivalent spellings
    // collapse to one key — matching the virtual FS — before the duplicate
    // check; otherwise the chosen file would depend on archive order.
    const relativePath = normalizeArchivePath(stripRoot(entry.name, root));
    if (!relativePath) continue;
    // Two entries must never resolve to the same path — otherwise the chosen
    // receipt would depend on archive order.
    if (files.has(relativePath)) {
      return {
        error: {
          code: "zip_duplicate_entry",
          message: `ZIP contains a duplicate path after unwrapping: ${relativePath}`
        }
      };
    }
    const text = await entry.async("text");
    files.set(relativePath, text);
  }

  if (files.size === 0) {
    return {
      error: {
        code: "zip_empty",
        message: "ZIP file contained no files."
      }
    };
  }

  return {
    receipt: {
      packageDirectory: RECEIPT_SYNTHETIC_ROOT,
      files,
      sourceLabel: file.name
    }
  };
}

function stripRoot(name: string, root: string): string {
  const normalized = name.replace(/^\/+/, "");
  if (root && normalized.startsWith(root)) {
    return normalized.slice(root.length);
  }
  return normalized;
}
